import * as React from 'react';
import { useSelector } from 'react-redux';

import { useMaxPinsPerCluster } from './useMaxPinsPerCluster';
import { IPinsLayer } from './usePinsLayer';
import { useHoveredOfferId } from '../../../../common/containers/OfferHoverProvider';
import { IRBushItem, TFeature } from '../../../../common/types/map';
import { IApplicationState } from '../../../../common/types/redux';
import { getTilesToUpdate } from '../../../../common/utils/tiles';
import { selectActiveFeature, selectMapBounds } from '../../../selectors';
import { TMapBounds } from '../../../types/mapBounds';

import type RBush from 'rbush';

interface IUseOfferHoverPinParams extends Pick<IPinsLayer, 'rbush' | 'pointsLayer' | 'balloonManager'> {
  isDetailsVisible: boolean;
}

// Ищем фичу наведённого оффера в rbush - источнике реально отрисованных пинов, а не в redux
// features[precision]: при скрытии/раскрытии листинга precision дрейфует, и redux-бакет текущего
// precision расходится с тем, что отрисовано. rbush всегда соответствует видимым пинам.
function findFeatureByOfferIdInRbush(rbush: RBush<IRBushItem>, offerId: number | null): TFeature | null {
  if (offerId === null) {
    return null;
  }

  const item = rbush
    .all()
    .find(rbushItem => rbushItem.feature.properties.offers.some(offer => offer.realtyId === offerId));

  return item?.feature ?? null;
}

export function useOfferHoverPin({
  rbush,
  pointsLayer,
  balloonManager,
  isDetailsVisible,
}: IUseOfferHoverPinParams): void {
  const selectedFeature = useSelector<IApplicationState, TFeature | null>(selectActiveFeature);
  const hoveredOfferId = useHoveredOfferId();
  const hoveredFeature = findFeatureByOfferIdInRbush(rbush, hoveredOfferId);
  const { bounds, zoom } = useSelector<IApplicationState, TMapBounds>(selectMapBounds);
  const maxPinsPerCluster = useMaxPinsPerCluster();

  const prevHoveredIdRef = React.useRef<string | null>(null);

  const toggleRef = React.useRef<(featureId: string, value: boolean) => void>(() => undefined);
  toggleRef.current = (featureId, value) => {
    if (!bounds) {
      return;
    }

    // поиск по id через полный обход: пин с большим смещением сетки может выходить
    // за bounds поиска, а объектов ≤ ~28
    const item = rbush.all().find(i => i.feature.id === featureId);

    if (!item) {
      return;
    }

    item.feature.properties = { ...item.feature.properties, isHovered: value };
    balloonManager.updateBalloonProperties(featureId, item.feature.properties, maxPinsPerCluster);

    const tiles = getTilesToUpdate({
      rbushItems: [item],
      bounds,
      zoom,
      includeShadow: isDetailsVisible,
    });
    pointsLayer.layer.update(tiles);
  };

  React.useEffect(() => {
    const nextId = hoveredFeature && hoveredFeature.id !== selectedFeature?.id ? (hoveredFeature.id as string) : null;
    const prevId = prevHoveredIdRef.current;

    if (prevId && prevId !== nextId) {
      toggleRef.current(prevId, false);
    }
    if (nextId && nextId !== prevId) {
      toggleRef.current(nextId, true);
    }

    prevHoveredIdRef.current = nextId;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hoveredOfferId, selectedFeature]);

  React.useEffect(
    () => () => {
      if (prevHoveredIdRef.current) {
        toggleRef.current(prevHoveredIdRef.current, false);
      }
    },
    [],
  );
}
